<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatSalary } from '@/composables/new-ui'

type CompanyVacancy = {
  id: string
  title: string
  city?: { title?: string | null } | null
  profession?: { title?: string | null } | null
  salary?: { min: number | null, max: number | null }
}

const props = defineProps<{
  companyId: string
  /** The vacancy on screen: never listed as «other». */
  vacancyId: string
  linkTo: (id: string) => RouteLocationRaw
}>()

const PAGE_SIZE = 10

const items = ref<CompanyVacancy[]>([])
const total = ref(0)
const page = ref(0)
const loading = ref(true)
const error = ref('')
let requestId = 0

const others = computed(() => items.value.filter(vacancy => vacancy.id !== props.vacancyId))
const hasMore = computed(() => items.value.length < total.value)

// The public search filtered by company: only published vacancies anyone may open.
async function load(nextPage = 1) {
  const current = ++requestId
  loading.value = true
  error.value = ''
  const result = await mainFetch('vacancies', {
    query: { type: 'search', companyId: props.companyId, perPage: PAGE_SIZE, page: nextPage },
  })
  if (current !== requestId) return
  const list = result.data.value?.vacancies
  if (result.error.value || !Array.isArray(list?.items)) {
    error.value = apiErrorMessage(result.error.value, 'Не удалось загрузить другие вакансии')
  } else {
    items.value = nextPage === 1 ? list.items : [...items.value, ...list.items]
    total.value = Number(list.count) || 0
    page.value = nextPage
  }
  loading.value = false
}

watch(() => [props.companyId, props.vacancyId], () => { void load() })
onMounted(() => { void load() })
</script>

<template>
  <!-- A company without other open vacancies shows nothing rather than an empty heading. -->
  <section
    v-if="loading || error || others.length > 0"
    class="flex flex-col gap-3 px-6 pb-6"
    aria-labelledby="listing-company-vacancies"
    data-testid="company-vacancies"
  >
    <h3 id="listing-company-vacancies" class="text-base font-semibold leading-6 text-foreground">
      Другие вакансии этой компании
    </h3>
    <ul v-if="others.length > 0" class="flex flex-col gap-2">
      <li v-for="vacancy in others" :key="vacancy.id">
        <NuxtLink
          :to="linkTo(vacancy.id)"
          class="flex items-center gap-3 rounded-2xl bg-field px-4 py-3 outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="truncate text-base font-medium leading-6 text-foreground">{{ vacancy.title || vacancy.profession?.title || 'Вакансия' }}</span>
            <span class="truncate text-sm leading-5 text-muted-foreground">
              {{ [formatSalary(vacancy.salary), vacancy.city?.title].filter(Boolean).join(' · ') }}
            </span>
          </span>
          <Icon name="tabler:chevron-right" class="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        </NuxtLink>
      </li>
    </ul>
    <div v-if="loading" class="flex flex-col gap-2" aria-live="polite">
      <Skeleton class="h-[4.5rem] rounded-2xl" />
      <Skeleton class="h-[4.5rem] rounded-2xl" />
      <span class="sr-only">Загружаем другие вакансии</span>
    </div>
    <div v-else-if="error" class="flex items-center justify-between gap-3 text-sm text-muted-foreground" role="alert">
      <span>{{ error }}</span>
      <Button variant="outline" size="sm" shape="pill" @click="load(page + 1)">Повторить</Button>
    </div>
    <Button v-else-if="hasMore" variant="outline" shape="pill" class="self-start" @click="load(page + 1)">Показать ещё</Button>
  </section>
</template>
